import { DateInput, TimeInput } from './DateTimeInputs';
import { activeLang, useT } from '../lib/i18n';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  BOOKING_FIELD_LABELS,
  FLAGS,
  MISSING_LABELS,
  TOUR_KIND_OPTIONS,
  TRANSFER_DIRECTION_LABELS,
  TRANSFER_DIRECTIONS,
  transferDirectionOf,
  missingInfo,
  displayName,
  hasOtherAlphabet,
  type BookingDto,
  type BookingEditable,
  type BookingStatus,
  type RefItem, translate
} from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { useRefData } from '../lib/reference';
import { longDate } from '../lib/dates';

type Form = Record<BookingEditable, unknown>;

const EMPTY = (date: string): Form => ({
  date, tourId: '', pickupTime: '', paxAdults: 2, paxChildren: 0, boosters: 0, babySeats: 0, clientName: '', clientNameLatin: '', phone: '',
  email: '', language: '', hotelId: '', hotelName: '', address: '', status: null, flag: '', cashReminder: '', notes: '',
  brandId: '', channelId: '', agencyId: '', agencyRef: '',
  transferDirection: '', transportRef: '', expectedTime: '', expectedChecked: false, pickupPlace: '', pickupAddress: '',
  dropoffPlace: '', dropoffAddress: '', groupRef: '',
});

/** Places a transfer often starts from or goes to (a known hotel can also be typed). */
const COMMON_PLACES = ['Nice Airport T1', 'Nice Airport T2', 'Gare Nice-Ville', 'Gare de Cannes', 'Gare de Monaco', 'Port de Nice', 'Port de Villefranche-sur-Mer', 'Port de Cannes'];

/** "Private Transfer from Airport Nice to Monaco/ Monte Carlo" → { from: "Airport Nice", to: "Monaco/ Monte Carlo" }. */
export function transferEnds(name: string): { from: string; to: string } | null {
  const m = /\bfrom\s+(.+?)\s+to\s+(.+)$/i.exec(name.trim());
  return m ? { from: m[1]!.trim(), to: m[2]!.trim() } : null;
}
// Transfers: the route only, no code (less noise, Bruno 19/10); the code stays in the tooltip.
const tourLabel = (tr: RefItem) => {
  const ends = tr.kind === 'transfer' ? transferEnds(String(tr.name ?? '')) : null;
  if (tr.kind === 'transfer') return ends ? `${ends.from} → ${ends.to}` : String(tr.name || tr.code);
  return `${String(tr.code)} · ${String(tr.name)}`;
};

const fromBooking = (b: BookingDto): Form => {
  const f = EMPTY(b.date);
  for (const k of Object.keys(f) as BookingEditable[]) {
    const v = (b as unknown as Record<string, unknown>)[k];
    f[k] = v === null || v === undefined ? (k === 'status' ? null : k === 'expectedChecked' ? false : '') : v;
  }
  return f;
};

interface HistoryRow { at: string; action: string; by: string; changes: Record<string, { from?: unknown; to?: unknown }> }

function Section({ title, children }: { title: string; children: ReactNode }) {
  const t = useT();
  return (
    <fieldset className="section-fs">
      <legend>{t(title)}</legend>
      <div className="grid2">{children}</div>
    </fieldset>
  );
}

/** Set by the open booking panel: true while it holds changes not saved yet. */
let panelDirty: () => boolean = () => false;

/**
 * Asked before the panel closes or shows another booking (0.37, Bruno 04/11): with unsaved changes,
 * the person confirms before they are discarded. Returns true when it is fine to leave.
 */
export function mayLeaveBookingPanel(): boolean {
  if (!panelDirty()) return true;
  return window.confirm(translate(activeLang, 'This booking has changes that are not saved. Discard them?'));
}

/** One line of a booking's history in plain words (0.37: platform, import and planning events had technical names). */
function historyText(h: HistoryRow, t: ReturnType<typeof useT>): string {
  const c = h.changes as Record<string, any>;
  const status = c.status && typeof c.status === 'object' ? ` (${String(c.status.from ?? '–')} → ${String(c.status.to ?? '–')})` : '';
  switch (h.action) {
    case 'create': return c.copyOf ? t('created it as a copy of {ref}', { ref: String(c.copyOf) }) : t('created it');
    case 'platform_create': return t('received it from the booking platform');
    case 'import': return t('imported it from Bingo App 2026');
    case 'duplicate': return t('copied it as {ref}', { ref: String(c.copy ?? '') });
    case 'plan_lock': return t('locked it in its vehicle');
    case 'plan_unlock': return t('unlocked it');
    case 'plan': {
      const where = (v: unknown) => (v ? String(v) : t('To place'));
      const how = c.by === 'auto-plan' ? ` (${t('Auto-plan')})` : c.by === 'clear' ? ` (${t('Clear')})` : '';
      return t('moved it: {from} → {to}', { from: where(c.vehicle?.from), to: where(c.vehicle?.to) }) + how;
    }
  }
  if (h.action === 'cancel' || h.action.endsWith('_cancel')) return t('cancelled it') + status;
  const fields = Object.keys(c).filter((k) => !['by', 'version'].includes(k))
    .map((k) => t((BOOKING_FIELD_LABELS as Record<string, string>)[k] ?? k)).join(', ');
  return t('changed {fields}', { fields }) + status;
}

export function BookingPanel({ booking, date, onClose, onSaved }: {
  booking: BookingDto | null; date: string; onClose: () => void; onSaved: (b: BookingDto) => void;
}) {
  const { lists, byId } = useRefData();
  const t = useT();
  const [form, setForm] = useState<Form>(() => (booking ? fromBooking(booking) : EMPTY(date)));
  const formRef = useRef(form);
  formRef.current = form;
  /** The form as last loaded or saved; any difference means unsaved changes. */
  const cleanForm = useRef(JSON.stringify(form));
  const saved = useRef(false);
  useEffect(() => {
    panelDirty = () => !saved.current && JSON.stringify(formRef.current) !== cleanForm.current;
    return () => { panelDirty = () => false; };
  }, []);
  const requestClose = () => { if (mayLeaveBookingPanel()) onClose(); };
  const [current, setCurrent] = useState<BookingDto | null>(booking);
  /** Set by "Duplicate": the booking being copied (the window becomes a new booking, saved only on Create). */
  const [copyOf, setCopyOf] = useState<BookingDto | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<HistoryRow[] | null>(null);
  const [hotelText, setHotelText] = useState(() => {
    if (!booking) return '';
    const h = booking.hotelId ? byId('hotels', booking.hotelId) : undefined;
    return h ? String(h.name) : booking.hotelName ?? '';
  });

  // On computers the window is a side panel: the page makes room for it and stays usable.
  useEffect(() => {
    const root = document.querySelector('.app-root');
    root?.classList.add('side-open');
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && mayLeaveBookingPanel()) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { root?.classList.remove('side-open'); window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  useEffect(() => {
    if (!current) return;
    api<{ history: HistoryRow[] }>(`/api/bookings/${current.id}/history`).then((r) => setHistory(r.history)).catch(() => setHistory([]));
  }, [current]);

  const set = (k: BookingEditable, v: unknown) => setForm((p) => ({ ...p, [k]: v }));
  const active = (items: RefItem[] | undefined, keep?: unknown) => (items ?? []).filter((x) => x.active || x.id === keep);

  const toursByKind = useMemo(() => {
    const groups = TOUR_KIND_OPTIONS.map((k) => ({ ...k, items: active(lists?.tours, form.tourId).filter((t) => t.kind === k.value) }));
    return groups.filter((g) => g.items.length);
  }, [lists, form.tourId]);

  const hotelsByName = useMemo(() => {
    const m = new Map<string, RefItem>();
    for (const h of lists?.hotels ?? []) if (h.active) m.set(String(h.name).toLowerCase(), h);
    return m;
  }, [lists]);

  function onHotelText(text: string) {
    setHotelText(text);
    const match = hotelsByName.get(text.trim().toLowerCase());
    if (match) {
      setForm((p) => ({ ...p, hotelId: match.id, hotelName: '', address: [match.address, match.town].filter(Boolean).join(', ') }));
    } else {
      setForm((p) => ({ ...p, hotelId: '', hotelName: text }));
    }
  }

  const tourOf = (id: unknown) => (id ? byId('tours', String(id)) : undefined);
  const isTransfer = tourOf(form.tourId)?.kind === 'transfer';
  const ends = isTransfer ? transferEnds(String(tourOf(form.tourId)?.name ?? '')) : null;
  /** Choosing a transfer tour makes the booking a transfer; its direction is read from the tour code. */
  function onTour(id: string) {
    const tr = tourOf(id);
    setForm((p) => ({
      ...p, tourId: id,
      transferDirection: tr?.kind === 'transfer' ? (p.transferDirection || transferDirectionOf({ code: String(tr.code), name: String(tr.name ?? '') })) : '',
    }));
  }
  /** A known hotel typed as a pickup or drop-off place fills its address. */
  function onPlace(which: 'pickup' | 'dropoff', text: string) {
    const h = hotelsByName.get(text.trim().toLowerCase());
    setForm((p) => ({
      ...p, [`${which}Place`]: text,
      ...(h ? { [`${which}Address`]: [h.address, h.town].filter(Boolean).join(', ') } : {}),
    }));
  }

  const missing = missingInfo({
    status: (form.status as BookingStatus | null) ?? null, tourId: (form.tourId as string) || null, pickupTime: (form.pickupTime as string) || null,
    phone: (form.phone as string) || null, hotelId: (form.hotelId as string) || null, hotelName: (form.hotelName as string) || null,
    address: (form.address as string) || null, clientName: String(form.clientName ?? ''),
    transferDirection: (form.transferDirection as BookingDto['transferDirection']) || null,
    pickupPlace: (form.pickupPlace as string) || null, pickupAddress: (form.pickupAddress as string) || null,
    dropoffPlace: (form.dropoffPlace as string) || null, dropoffAddress: (form.dropoffAddress as string) || null,
  });

  function payload(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, raw] of Object.entries(form) as [BookingEditable, unknown][]) {
      let v: unknown = raw === '' ? null : raw;
      if (['paxAdults', 'paxChildren', 'boosters', 'babySeats'].includes(k)) v = Number(raw || 0);
      if (k === 'cashReminder') v = raw === '' || raw === null ? null : Number(raw);
      if (k === 'clientName') v = String(raw ?? '');
      if (current) {
        const before = (current as unknown as Record<string, unknown>)[k] ?? null;
        if (JSON.stringify(before) === JSON.stringify(v)) continue;
      } else if (v === null) continue;
      out[k] = v;
    }
    return out;
  }

  async function save(e?: FormEvent, extra?: Record<string, unknown>) {
    e?.preventDefault();
    setBusy(true);
    setError('');
    try {
      const body = { ...payload(), ...extra };
      const r = current
        ? await api<{ booking: BookingDto }>(`/api/bookings/${current.id}`, { method: 'PATCH', body: { ...body, version: current.version } })
        : await api<{ booking: BookingDto }>('/api/bookings', { method: 'POST', body: copyOf ? { ...body, duplicatedFrom: copyOf.id } : body });
      saved.current = true;
      onSaved(r.booking);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'stale' && current) {
        // Someone else saved first: show their version, keep nothing silently.
        const fresh = await api<{ booking: BookingDto }>(`/api/bookings/${current.id}`);
        setCurrent(fresh.booking);
        setForm(fromBooking(fresh.booking));
        cleanForm.current = JSON.stringify(fromBooking(fresh.booking));
        onSaved(fresh.booking);
      }
      setError(err instanceof ApiError ? err.message : t('Could not save.'));
    } finally {
      setBusy(false);
    }
  }

  const diffs = current?.platformDiffs ?? {};
  const platformNote = (k: string) =>
    k in diffs ? <span className="hint warn-text">{t('Platform says: {value}', { value: String(diffs[k] ?? t('empty')) })}</span> : null;

  /**
   * Duplicate (Bruno, 19/10): a tour is copied whole; a transfer keeps only the client (the same client
   * often books another route). Never copied: status, vehicle, lock, platform link, reference, history.
   */
  function duplicate() {
    if (!current) return;
    const base = fromBooking(current);
    const transfer = tourOf(current.tourId)?.kind === 'transfer';
    setForm({
      ...base,
      status: null,
      ...(transfer
        ? {
            tourId: '', pickupTime: '', transferDirection: '', transportRef: '', expectedTime: '', expectedChecked: false,
            pickupPlace: '', pickupAddress: '', dropoffPlace: '', dropoffAddress: '', groupRef: '',
          }
        : {}),
    });
    setCopyOf(current);
    setCurrent(null);
    setHistory(null);
    setError('');
  }

  const title = current ? `${current.reference} · ${displayName(current) || t('No name')}`
    : copyOf ? t('New booking · copy of {ref}', { ref: copyOf.reference }) : t('New booking');

  return (
    <div className="sheet-backdrop booking-backdrop" onClick={requestClose}>
      <form className="sheet sheet-wide card booking-modal" onClick={(e) => e.stopPropagation()} onSubmit={save} aria-label={title}>
        <div className="sheet-head">
          <div style={{ marginRight: 'auto', minWidth: 0 }}>
            <h2 className="ellipsis">{title}</h2>
            {current && <div className="muted" style={{ fontSize: 13 }}>{longDate(current.date)} · {current.source === 'manual' ? t('Typed by hand') : t('From {source}', { source: current.source })}{current.externalRef ? ` · ${current.externalRef}` : ''}</div>}
          </div>
          <button type="button" className="btn btn-sm btn-ghost" onClick={requestClose}>{t('Close')}</button>
        </div>

        <div className="sheet-body">
          {error && <div className="alert alert-bad" role="alert">{error}</div>}
          {copyOf && (
            <div className="alert alert-info">
              {t(tourOf(copyOf.tourId)?.kind === 'transfer'
                ? 'Copy of {ref}: the client is copied; choose the service, the route and the times. Nothing is saved before Create.'
                : 'Copy of {ref}: check the date. Nothing is saved before Create.', { ref: copyOf.reference })}{' '}
              {t('The copy is typed by hand (not linked to the platform), not contacted, and to place.')}
            </div>
          )}
          {missing.length > 0 && (
            <div className="alert alert-warn">{t('Missing:')} {missing.map((m) => t(MISSING_LABELS[m])).join(' · ')}</div>
          )}

          <div className="field">
            <label>{t('Status')}</label>
            <div className="seg seg-status" role="group" aria-label={t('Status')}>
              {([null, 'C', 'CC', 'X'] as (BookingStatus | null)[]).map((s) => (
                <button key={s ?? 'none'} type="button" aria-pressed={form.status === s} className={`st-${s ?? 'none'}`}
                  onClick={() => set('status', s)}>
                  {t(s === null ? 'Not contacted' : s === 'C' ? 'C · contacted' : s === 'CC' ? 'CC · confirmed' : 'X · cancelled')}
                </button>
              ))}
            </div>
          </div>

          {isTransfer ? (
            <fieldset className="section-fs tr-block">
              <legend className="tr-legend">{t('Transfer')}</legend>
              <datalist id="place-list">
                {COMMON_PLACES.map((p) => <option key={p} value={p} />)}
                {(lists?.hotels ?? []).filter((h) => h.active).map((h) => <option key={h.id} value={String(h.name)}>{String(h.town ?? '')}</option>)}
              </datalist>
              <div className="tr-rows">
                <div className="tr-row tr-row-service">
                  <div className="field"><label htmlFor="b-tour">{t('Service')}</label>
                    <select id="b-tour" className="select" value={String(form.tourId)} onChange={(e) => onTour(e.target.value)}>
                      <option value="">{t('Choose a tour…')}</option>
                      {toursByKind.map((g) => (
                        <optgroup key={g.value} label={t(g.label)}>
                          {g.items.map((t) => <option key={t.id} value={t.id} title={String(t.code)}>{tourLabel(t)}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    {platformNote('tourId')}</div>
                  <div className="field"><label htmlFor="b-dir">{t('Direction')}</label>
                    <select id="b-dir" className="select" value={String(form.transferDirection ?? '')} onChange={(e) => set('transferDirection', e.target.value)}>
                      {TRANSFER_DIRECTIONS.map((d) => <option key={d} value={d}>{t(TRANSFER_DIRECTION_LABELS[d])}</option>)}
                    </select></div>
                </div>
                <div className="tr-row tr-row-end">
                  <span className="tr-end">{t('From')}</span>
                  <input id="b-pp" className="input" list="place-list" aria-label={t('Pickup place')} placeholder={ends?.from ?? t('Pickup place')}
                    value={String(form.pickupPlace ?? '')} onChange={(e) => onPlace('pickup', e.target.value)} />
                  <input id="b-pa" className="input" aria-label={t('Pickup address')} placeholder={t('Pickup address')}
                    value={String(form.pickupAddress ?? '')} onChange={(e) => set('pickupAddress', e.target.value)} />
                </div>
                <div className="tr-row tr-row-end">
                  <span className="tr-end">{t('To')}</span>
                  <input id="b-dp" className="input" list="place-list" aria-label={t('Drop-off place')} placeholder={ends?.to ?? t('Drop-off place')}
                    value={String(form.dropoffPlace ?? '')} onChange={(e) => onPlace('dropoff', e.target.value)} />
                  <input id="b-da" className="input" aria-label={t('Drop-off address')} placeholder={t('Drop-off address')}
                    value={String(form.dropoffAddress ?? '')} onChange={(e) => set('dropoffAddress', e.target.value)} />
                </div>
                <div className="tr-row tr-row-times">
                  <div className="field"><label htmlFor="b-date">{t('Date')}</label>
                    <DateInput id="b-date" required value={String(form.date)} onChange={(v) => set('date', v)} /></div>
                  <div className="field"><label htmlFor="b-pickup">{t('Official time')}</label>
                    <TimeInput id="b-pickup" value={String(form.pickupTime ?? '')} onChange={(v) => set('pickupTime', v)} />
                    {platformNote('pickupTime')}</div>
                  <div className="field"><label htmlFor="b-exp">{t('Expected (Prévu)')}</label>
                    <TimeInput id="b-exp" value={String(form.expectedTime ?? '')} onChange={(v) => set('expectedTime', v)} /></div>
                  <div className="field"><label htmlFor="b-chk">{t('Checked')}</label>
                    <label className="check tr-check" title={t('Flight or train checked')}><input id="b-chk" type="checkbox" checked={!!form.expectedChecked} onChange={(e) => set('expectedChecked', e.target.checked)} /> {t('Yes')}</label></div>
                </div>
                <span className="hint">{t('Prévu: the time after checking the flight or train; empty = the official time.')}</span>
                <div className="tr-row tr-row-half">
                  <div className="field"><label htmlFor="b-tr">{t('Flight or train')}</label>
                    <input id="b-tr" className="input" value={String(form.transportRef ?? '')} placeholder="AF 7700 · TGV 6173" onChange={(e) => set('transportRef', e.target.value)} /></div>
                  <div className="field"><label htmlFor="b-grp">{t('Group')}</label>
                    <input id="b-grp" className="input" value={String(form.groupRef ?? '')} title={t('Bookings with the same group travel together.')} onChange={(e) => set('groupRef', e.target.value)} /></div>
                </div>
              </div>
            </fieldset>
          ) : (
          <Section title="Tour">
            <div className="field span2">
              <label htmlFor="b-tour">{t('Tour')}</label>
              <select id="b-tour" className="select" value={String(form.tourId)} onChange={(e) => onTour(e.target.value)}>
                <option value="">{t('Choose a tour…')}</option>
                {toursByKind.map((g) => (
                  <optgroup key={g.value} label={t(g.label)}>
                    {g.items.map((t) => <option key={t.id} value={t.id} title={String(t.code)}>{tourLabel(t)}</option>)}
                  </optgroup>
                ))}
              </select>
              {platformNote('tourId')}
            </div>
            <div className="field">
              <label htmlFor="b-date">{t('Date')}</label>
              <DateInput id="b-date" required value={String(form.date)} onChange={(v) => set('date', v)} />
            </div>
            <div className="field">
              <label htmlFor="b-pickup">{t('Pickup time')}</label>
              <TimeInput id="b-pickup" value={String(form.pickupTime ?? '')} onChange={(v) => set('pickupTime', v)} />
              {platformNote('pickupTime')}
            </div>
          </Section>
          )}

          <Section title="Passengers">
            <div className="field"><label htmlFor="b-ad">{t('Adults')}</label>
              <input id="b-ad" className="input" type="number" inputMode="numeric" min={0} max={99} value={String(form.paxAdults)} onChange={(e) => set('paxAdults', e.target.value)} />
              {platformNote('paxAdults')}</div>
            <div className="field"><label htmlFor="b-ch">{t('Children')}</label>
              <input id="b-ch" className="input" type="number" inputMode="numeric" min={0} max={99} value={String(form.paxChildren)} onChange={(e) => set('paxChildren', e.target.value)} /></div>
            <div className="field"><label htmlFor="b-boo">{t('Boosters')}</label>
              <input id="b-boo" className="input" type="number" inputMode="numeric" min={0} max={20} value={String(form.boosters)} onChange={(e) => set('boosters', e.target.value)} /></div>
            <div className="field"><label htmlFor="b-bs">{t('Baby seats')}</label>
              <input id="b-bs" className="input" type="number" inputMode="numeric" min={0} max={20} value={String(form.babySeats)} onChange={(e) => set('babySeats', e.target.value)} /></div>
          </Section>

          <Section title="Client">
            <div className="field span2"><label htmlFor="b-name">{t('Name')}</label>
              <input id="b-name" className="input" value={String(form.clientName)} onChange={(e) => set('clientName', e.target.value)} />
              {platformNote('clientName')}</div>
            {(hasOtherAlphabet(String(form.clientName)) || !!form.clientNameLatin) && (
              <div className="field span2"><label htmlFor="b-latin">{t('Name in Latin letters')}</label>
                <input id="b-latin" className="input" value={String(form.clientNameLatin ?? '')} placeholder={t('Filled in automatically after saving')}
                  onChange={(e) => set('clientNameLatin', e.target.value)} />
                <span className="hint">{t('Shown on the screens and the BDR. Written automatically from the name; a correction made here is kept.')}</span></div>
            )}
            <div className="field"><label htmlFor="b-phone">{t('Phone')}</label>
              <input id="b-phone" className="input" type="tel" value={String(form.phone ?? '')} onChange={(e) => set('phone', e.target.value)} />
              {platformNote('phone')}</div>
            <div className="field"><label htmlFor="b-lang">{t('Language')}</label>
              <select id="b-lang" className="select" value={String(form.language ?? '')} onChange={(e) => set('language', e.target.value)}>
                <option value="">—</option>
                {active(lists?.languages, undefined).map((l) => <option key={l.id} value={String(l.code)}>{String(l.code)} · {String(l.label)}</option>)}
              </select></div>
            <div className="field span2"><label htmlFor="b-email">{t('Email')}</label>
              <input id="b-email" className="input" type="email" value={String(form.email ?? '')} onChange={(e) => set('email', e.target.value)} /></div>
          </Section>

          {!isTransfer && (
          <Section title="Pickup place">
              <div className="field span2"><label htmlFor="b-hotel">{t('Hotel')}</label>
                <input id="b-hotel" className="input" list="hotel-list" value={hotelText} placeholder={t('Start typing the hotel name')}
                  onChange={(e) => onHotelText(e.target.value)} />
                <datalist id="hotel-list">
                  {(lists?.hotels ?? []).filter((h) => h.active).map((h) => <option key={h.id} value={String(h.name)}>{String(h.town ?? '')}</option>)}
                </datalist>
                <span className="hint">{form.hotelId ? t('Known hotel: address filled from the list.') : hotelText ? t('Not in the list: kept as written.') : ''}</span>
                {platformNote('hotelName')}</div>
              <div className="field span2"><label htmlFor="b-addr">{t('Address')}</label>
                <input id="b-addr" className="input" value={String(form.address ?? '')} onChange={(e) => set('address', e.target.value)} />
                {platformNote('address')}</div>
            </Section>
          )}

          <Section title="Sold by">
            <div className="field"><label htmlFor="b-chan">{t('Channel')}</label>
              <select id="b-chan" className="select" value={String(form.channelId ?? '')} onChange={(e) => {
                const c = byId('channels', e.target.value);
                setForm((p) => ({ ...p, channelId: e.target.value, brandId: (c?.brandId as string) || p.brandId }));
              }}>
                <option value="">—</option>
                {active(lists?.channels, form.channelId).map((c) => <option key={c.id} value={c.id}>{String(c.code)} · {String(c.name)}</option>)}
              </select></div>
            <div className="field"><label htmlFor="b-brand">{t('Brand')}</label>
              <select id="b-brand" className="select" value={String(form.brandId ?? '')} onChange={(e) => set('brandId', e.target.value)}>
                <option value="">—</option>
                {active(lists?.brands, form.brandId).map((b) => <option key={b.id} value={b.id}>{String(b.name)}</option>)}
              </select></div>
            <div className="field"><label htmlFor="b-ag">{t('Agency')}</label>
              <select id="b-ag" className="select" value={String(form.agencyId ?? '')} onChange={(e) => set('agencyId', e.target.value)}>
                <option value="">—</option>
                {active(lists?.agencies, form.agencyId).map((a) => <option key={a.id} value={a.id}>{String(a.name)}</option>)}
              </select></div>
            <div className="field"><label htmlFor="b-agref">{t('Agency reference')}</label>
              <input id="b-agref" className="input" value={String(form.agencyRef ?? '')} onChange={(e) => set('agencyRef', e.target.value)} /></div>
          </Section>

          <Section title="Office">
            <div className="field"><label htmlFor="b-flag">{t('Flag')}</label>
              <select id="b-flag" className="select" value={String(form.flag ?? '')} onChange={(e) => set('flag', e.target.value)}>
                <option value="">{t('None')}</option>
                {FLAGS.map((f) => <option key={f.key} value={f.key}>{t(f.label)}</option>)}
              </select></div>
            <div className="field"><label htmlFor="b-cash">{t('Cash to collect (€)')}</label>
              <input id="b-cash" className="input" type="number" inputMode="decimal" min={0} step="0.01" value={String(form.cashReminder ?? '')}
                onChange={(e) => set('cashReminder', e.target.value)} />
              <span className="hint">{t('A reminder for the guide; never added up.')}</span></div>
            <div className="field span2"><label htmlFor="b-notes">{t('Notes')}</label>
              <textarea id="b-notes" className="input textarea" rows={3} value={String(form.notes ?? '')} onChange={(e) => set('notes', e.target.value)} /></div>
          </Section>

          {current && (
            <div className="history span-all">
              <h3>{t('History')}</h3>
              {history === null ? <span className="muted">{t('Loading…')}</span> : (
                <ul>
                  {history.map((h, i) => (
                    <li key={i}>
                      <span className="muted">{new Date(h.at).toLocaleString(activeLang === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' })}</span>{' '}
                      <b>{h.by === 'platform' ? t('Platform') : h.by === 'former user' ? t('Former user') : h.by}</b> {historyText(h, t)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="sheet-foot">
          <button className="btn btn-primary" disabled={busy}>{current ? t('Save') : t('Create booking')}</button>
          <button type="button" className="btn" onClick={requestClose}>{t('Cancel')}</button>
          {current && (
            <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={duplicate} disabled={busy}
              title={t('Opens a new booking filled from this one; nothing is saved before Create')}>{t('Duplicate')}</button>
          )}
        </div>
      </form>
    </div>
  );
}
